// src/oura/components/fixes/ByWhoMovesNext.tsx
//
// The default Fixes view (D7): the whole plan as one list, grouped by who
// moves next. Three tiles say where the work stands; the list below holds
// proposals waiting on a decision, then what is in progress, what waits on
// the client, what is done, what has not started, and last the
// recommendations the plan has no step for.
//
// Every group folds by its head, as the Client tab's do (YW, 29 Sep 2026).
// The groups someone acts on start open. Not started and Not in the plan
// start folded, each head saying the one fact worth knowing while folded, and
// a hand fold of those two is kept per business. A deep link opens the group
// its row sits in (Tracker.tsx).
//
// It reads the same tasks, statuses and handlers as the week plan, so the two
// views can never disagree about a task.

import type { ReactNode } from "react";
import { CheckCircle2Icon, ClipboardListIcon, UserRoundIcon, WrenchIcon } from "lucide-react";
import type { AuditIssue } from "../../audit";
import type { TaskStatus, TrackerSuggestion, TrackerTask } from "../../tracker";
import { cn } from "../../lib/cn";
import { ArrowLink, FoldCard, GroupFold, LATE_TEXT, OfValue, PillButton, ShowMore, Tile, Tiles, TYPE, type StaffStatus } from "@oura/ui";
import { FixLine, FixRow, PlanGapRow, ProposedRow } from "./FixRows";
import type { ClientAsks } from "./useFixesData";
import {
    byUrgency,
    checkKeyOfRec,
    dayDate,
    daysLate,
    fixesGroupId,
    whyLine,
    whoMovesNext,
    type FixesGroup,
    type MovesNextContext,
    type PlacedTask,
    type RecommendationJoin,
} from "./model";

/** Done work shown before "Show N more": the latest proof, not the whole history. */
const DONE_SHOWN = 2;

/** "Questions", as the Client tab's card is named (YW, 29 Sep 2026). */
function asksOnClient(n: number): string {
    return `${n} ${n === 1 ? "question" : "questions"} on Client`;
}

/** "2 medium, 3 low priority": what a folded Not in the plan holds. */
function priorityNote(recs: RecommendationJoin["unplanned"]): string {
    const parts = (["high", "medium", "low"] as const)
        .map((p) => ({ p, n: recs.filter((r) => r.priority === p).length }))
        .filter(({ n }) => n > 0)
        .map(({ p, n }) => `${n} ${p}`);
    return `${parts.join(", ")} priority`;
}

export function ByWhoMovesNext({
    businessId,
    tasks,
    ctx,
    asks,
    recJoin,
    suggestions,
    issueByKey,
    highlightId,
    onOpen,
    onSetStatus,
    onDecideSuggestion,
    onRebuild,
    rebuilding,
    onOpenIssue,
    onOpenClient,
}: {
    /** Keys each group's hand fold, so a fold on one client does not carry to the next. */
    businessId: string;
    /** Every task in the plan, in the block it sits in now, removed ones excluded. */
    tasks: PlacedTask[];
    ctx: MovesNextContext;
    /** The asks waiting on the client; null while unknown, which says nothing about them. */
    asks: ClientAsks | null;
    recJoin: RecommendationJoin;
    /** Proposals still waiting on a decision. */
    suggestions: TrackerSuggestion[];
    issueByKey: Map<string, AuditIssue>;
    highlightId: string | null;
    onOpen: (t: PlacedTask) => void;
    onSetStatus: (t: TrackerTask, s: TaskStatus) => void;
    /** Absent (a fixture, or no assessment) hides the proposals. */
    onDecideSuggestion?: (id: string, decision: "keep" | "dismiss") => Promise<boolean>;
    /** Absent hides Refresh plan beside the recommendations with no step. */
    onRebuild?: () => void;
    rebuilding: boolean;
    onOpenIssue?: (issueKey: string) => void;
    /**
     * Open the Client screen on one card: Questions to the client when some
     * wait on the client, else Documents, where the client is asked now.
     * Absent hides the link.
     */
    onOpenClient?: (card: "questions" | "documents") => void;
}) {
    const { today } = ctx;
    const order = byUrgency(recJoin.byTask, today);
    const groups: Record<StaffStatus, PlacedTask[]> = { in_progress: [], needs_client: [], done: [], not_started: [] };
    for (const t of tasks) groups[whoMovesNext(t, ctx)].push(t);
    groups.in_progress.sort(order);
    groups.needs_client.sort(order);
    groups.not_started.sort(order);

    // A touchpoint of the client's that is not due yet waits under Not
    // started, but it is not ours to move, so Needs us leaves it out.
    const notDueClient = groups.not_started.filter((t) => t.owner === "client").length;
    const needsUs = groups.in_progress.length + groups.not_started.length - notDueClient;
    // "Now" is the first job in progress in plan order, not the most urgent:
    // it names what somebody is actually working on.
    const now = groups.in_progress.slice().sort((a, b) => a.block - b.block)[0];
    const clientLate = groups.needs_client.filter((t) => daysLate(t, today) > 0).length;
    const open = tasks.length - groups.done.length;
    const proposed = onDecideSuggestion ? suggestions : [];
    const gaps = recJoin.unplanned;

    const issueOf = (t: TrackerTask) => (t.source?.type === "check" && t.source.ref ? issueByKey.get(t.source.ref) : undefined);
    const row = (t: PlacedTask, group: StaffStatus) => (
        <FixRow
            key={t.id}
            task={t}
            group={group}
            why={whyLine(t, issueOf(t))}
            today={today}
            highlighted={highlightId === t.id}
            onOpen={() => onOpen(t)}
            onSetStatus={onSetStatus}
        />
    );
    const line = (t: PlacedTask, continued?: boolean) => (
        <FixLine key={t.id} task={t} continued={continued} highlighted={highlightId === t.id} onOpen={() => onOpen(t)} />
    );
    /** A line at the top of an open group, lined up with the rows' text. */
    const groupLine = (text: string) => <p className={cn(TYPE.tm, "m-0 pb-1 pl-5 text-gray-500")}>{text}</p>;
    /** A folded group never hides what is late. */
    const lateNote = (n: number): ReactNode => (n > 0 ? <span className={LATE_TEXT}>{n} late</span> : undefined);
    const foldId = (g: FixesGroup) => fixesGroupId(businessId, g);

    // Folded, Not started says what is late in it, or else when the next of
    // it falls due: the one reason to open it now. A note that is not a late
    // count shows from 640px only: on a phone it squeezed the group's own name
    // onto two lines, and the count and chevron already say what is inside.
    const notStartedLate = groups.not_started.filter((t) => daysLate(t, today) > 0).length;
    const nextDue = groups.not_started
        .map((t) => t.targetDate)
        .filter((d): d is string => Boolean(d))
        .sort()[0];
    const notStartedNote: ReactNode = notStartedLate > 0
        ? lateNote(notStartedLate)
        : nextDue
            ? <span className="hidden text-gray-500 sm:inline">Next due {dayDate(nextDue)}</span>
            : undefined;

    return (
        <div className="space-y-5">
            <Tiles cols={3}>
                <Tile
                    label="Needs us"
                    icon={{ icon: WrenchIcon, area: "work" }}
                    value={needsUs}
                    sub={<span className="block truncate">{now ? `Now: ${now.action}` : "Nothing in progress"}</span>}
                />
                <Tile
                    label="Waiting on client"
                    icon={{ icon: UserRoundIcon, area: "asks" }}
                    value={groups.needs_client.length}
                    // The number counts tasks; Overview and Client count
                    // questions. Said here, with the way to the questions, so
                    // the two can be told apart rather than read as a
                    // disagreement. With none waiting, the link opens the
                    // documents instead, where the client is asked now.
                    sub={
                        <>
                            Tasks, {clientLate ? <span className={LATE_TEXT}>{clientLate} late</span> : "none late"}.
                            {asks && onOpenClient && (
                                <>
                                    {" "}
                                    {asks.count > 0 ? (
                                        <ArrowLink onClick={() => onOpenClient("questions")}>{asksOnClient(asks.count)}</ArrowLink>
                                    ) : (
                                        <ArrowLink onClick={() => onOpenClient("documents")}>Open the documents</ArrowLink>
                                    )}
                                </>
                            )}
                        </>
                    }
                />
                <Tile
                    label="Done"
                    icon={{ icon: CheckCircle2Icon, area: "work" }}
                    value={<OfValue n={groups.done.length} of={tasks.length} />}
                    sub="Re-measured at the next scan"
                />
            </Tiles>

            <FoldCard id="fixes" title="Fixes" icon={{ icon: ClipboardListIcon, area: "work" }} foldedFact={`${open} open`}>
                {tasks.length === 0 && proposed.length === 0 && gaps.length === 0 ? (
                    <p className={cn(TYPE.tb, "m-0 text-gray-500")}>Nothing in this plan yet.</p>
                ) : (
                    // The groups are the only children of this box, so the first loses its divider.
                    <div>
                        {proposed.length > 0 && onDecideSuggestion && (
                            <GroupFold id={foldId("proposed")} lead="icon" tone="blue" title="Proposed" count={proposed.length}>
                                <div>
                                    {proposed.map((sg) => <ProposedRow key={sg.id} suggestion={sg} onDecide={onDecideSuggestion} />)}
                                </div>
                            </GroupFold>
                        )}
                        {groups.in_progress.length > 0 && (
                            <GroupFold
                                id={foldId("in_progress")}
                                lead="dot"
                                tone="blue"
                                title="In progress"
                                count={groups.in_progress.length}
                                note={lateNote(groups.in_progress.filter((t) => daysLate(t, today) > 0).length)}
                            >
                                <div>{groups.in_progress.map((t) => row(t, "in_progress"))}</div>
                            </GroupFold>
                        )}
                        {groups.needs_client.length > 0 && (
                            <GroupFold
                                id={foldId("needs_client")}
                                lead="dot"
                                tone="amber"
                                title="Waiting on client"
                                count={groups.needs_client.length}
                                note={lateNote(clientLate)}
                            >
                                {groupLine("A fix moves here when the client owns it and it is due, or an ask waits on them.")}
                                <div>{groups.needs_client.map((t) => row(t, "needs_client"))}</div>
                            </GroupFold>
                        )}
                        {groups.done.length > 0 && (
                            <GroupFold id={foldId("done")} lead="dot" tone="green" title="Done" count={groups.done.length}>
                                <div>
                                    {groups.done.slice(0, DONE_SHOWN).map((t) => line(t))}
                                    <ShowMore count={groups.done.length - DONE_SHOWN}>
                                        {groups.done.slice(DONE_SHOWN).map((t, i) => line(t, i === 0))}
                                    </ShowMore>
                                </div>
                            </GroupFold>
                        )}
                        {groups.not_started.length > 0 && (
                            <GroupFold
                                id={foldId("not_started")}
                                lead="dot"
                                tone="gray"
                                title="Not started"
                                count={groups.not_started.length}
                                defaultFolded
                                note={notStartedNote}
                            >
                                {notDueClient > 0
                                    && groupLine(`${notDueClient} of these ${notDueClient === 1 ? "is" : "are"} the client's and not due yet.`)}
                                <div>{groups.not_started.map((t) => row(t, "not_started"))}</div>
                            </GroupFold>
                        )}
                        {gaps.length > 0 && (
                            <GroupFold
                                id={foldId("not_in_plan")}
                                lead="dot"
                                tone="gray"
                                title="Not in the plan"
                                count={gaps.length}
                                defaultFolded
                                note={<span className="hidden text-gray-500 sm:inline">{priorityNote(gaps)}</span>}
                            >
                                {/* What lands here and the way to act on it, first: the
                                    head is one button, so Refresh plan cannot sit in it. */}
                                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pb-1 pl-5">
                                    <p className={cn(TYPE.tm, "m-0 min-w-0 flex-1 basis-48 text-gray-500")}>
                                        Recommendations from the scan that no step in the plan covers yet.
                                    </p>
                                    {onRebuild && (
                                        <PillButton variant="text" size="sm" busy={rebuilding} onClick={onRebuild}>
                                            {rebuilding ? "Refreshing" : "Refresh plan"}
                                        </PillButton>
                                    )}
                                </div>
                                <div>
                                    {gaps.map((rec) => {
                                        const key = checkKeyOfRec(rec.id);
                                        return (
                                            <PlanGapRow
                                                key={rec.id}
                                                rec={rec}
                                                onOpenCheck={key && onOpenIssue ? () => onOpenIssue(key) : undefined}
                                            />
                                        );
                                    })}
                                </div>
                            </GroupFold>
                        )}
                    </div>
                )}
            </FoldCard>
        </div>
    );
}
